import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ClipboardList,
  Mail,
  Monitor,
  Pencil,
  Search,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import InputCst from "@components/InputCst/InputCst";
import SortMenuCst from "@components/SortMenuCst";
import PaginatorCst from "@components/PaginatorCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { PagesID } from "@utils/utilsConfigurations";
import {
  PERMISSIONS,
  PermissionGate,
  editionPerm,
  usePermissions,
} from "@utils/permissions";
import { ServicesURL } from "@utils/utilsApi";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { openToast } from "@utils/toastStore";
import { ListItemAction } from "@components/ListItemCardCst/types";
import { openEditionEnrollmentModal } from "./EditionEnrollment/host";
import { useEditionsList } from "./hooks/useEditionsList";
import { useDeleteEdition } from "./hooks/useDeleteEdition";
import { useEditionMailto } from "./hooks/useEditionMailto";
import EditionCard from "./components/EditionCard";
import { EditionItem, EditionKind } from "./types";

const PAGE_SIZE = 20;
// Ricerca disabilitata finche' l'API di ricerca non e' pronta: per ora si usano
// i filtri canonici (accordion). Rimettere a true per riattivare la barra.
const EDITIONS_SEARCH_ENABLED = false;

const EditionsListPage: React.FC = () => {
  const { t } = useTranslation("editions");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", idLO = "" } = useParams();
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const { items, totalItems, totalPages, isLoading, mutate } = useEditionsList({
    corporateId,
    objectId: Number(idLO),
    page,
    pageSize,
    search: search || undefined,
    sort: sort || undefined,
    fieldFilters,
  });

  const kindParam = searchParams.get("kind");
  const kind: EditionKind =
    kindParam === "physical"
      ? "physical"
      : kindParam === "virtual"
        ? "virtual"
        : items[0]?.edition === "PHYSICAL"
          ? "physical"
          : "virtual";

  const { dataDetail: nonDeletableIds } = useFetchDataDetail(
    ServicesURL.getEditionsNotDeletable,
  );
  const nonDeletableSet = useMemo<Set<number | string>>(
    () => new Set(Array.isArray(nonDeletableIds) ? nonDeletableIds : []),
    [nonDeletableIds],
  );

  const { can } = usePermissions();
  const { deleteEdition, isDeleting } = useDeleteEdition(kind);
  const { sendMail, isPreparing: isPreparingMail } = useEditionMailto({
    corporateId,
  });

  const [editionToDelete, setEditionToDelete] = useState<EditionItem | null>(
    null,
  );

  const sortOptions = useMemo(
    () => [
      { value: "most-recent", label: String(t("sort.most-recent")) },
      { value: "least-recent", label: String(t("sort.least-recent")) },
    ],
    [t],
  );

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSearch(searchInput.trim());
      setPage(1);
    }
  };

  const editionKind = (edition: EditionItem): EditionKind =>
    edition.edition === "VIRTUAL" ? "virtual" : "physical";

  const goToDetail = (edition: EditionItem, tab?: string) => {
    const k = editionKind(edition);
    const tabQS = tab ? `&tab=${tab}` : "";
    navigate(
      `/v2/corporate/${corporateId}/detail/catalog/activities/${idLO}/editions/${edition.id}?kind=${k}${tabQS}`,
    );
  };

  const handleManage = (edition: EditionItem) => goToDetail(edition);

  const handleCreate = () => {
    navigate(
      `/v2/corporate/${corporateId}/detail/catalog/activities/${idLO}/editions/add?kind=${kind}`,
    );
  };

  const handleEnroll = (edition: EditionItem) => {
    const objectId = edition.learning_objects?.id ?? Number(idLO);
    openEditionEnrollmentModal({
      editionId: edition.id,
      objectId,
      corporateId,
      objectTitle:
        edition.learning_objects?.label || edition.edition_name || undefined,
      onSuccess: () => mutate(),
    });
  };

  const handleSendEmail = async (edition: EditionItem) => {
    const outcome = await sendMail({
      editionId: edition.id,
      kind: editionKind(edition),
    });
    if (outcome === "URL_TOO_LONG") {
      openToast({
        severity: "warning",
        message: String(t("menu.mail.url-too-long")),
      });
    } else if (outcome === "ERROR") {
      openToast({
        severity: "error",
        message: String(t("menu.mail.error")),
      });
    }
  };

  const handleEdit = (edition: EditionItem) => {
    const k = editionKind(edition);
    navigate(
      `/v2/corporate/${corporateId}/detail/catalog/activities/${idLO}/editions/${edition.id}/edit?kind=${k}`,
    );
  };

  const handleConfirmDelete = async () => {
    if (!editionToDelete) return;
    try {
      await deleteEdition({
        editionId: editionToDelete.id,
        corporateId,
      });
      openToast({
        severity: "success",
        message: String(t("menu.delete.success")),
      });
      mutate();
    } catch {
      openToast({
        severity: "error",
        message: String(t("menu.delete.error")),
      });
    } finally {
      setEditionToDelete(null);
    }
  };

  const buildMenuActions = (
    edition: EditionItem,
  ): ListItemAction[][] | undefined => {
    const k = editionKind(edition);
    const canEnroll = can(editionPerm(k, "enrollment"));
    const canSendMail = can(PERMISSIONS.enrollment.read);
    const canViewDetail = can(editionPerm(k, "retrieve"));
    const canViewRegistry = can(PERMISSIONS.registry.read);
    const canUpdate = can(editionPerm(k, "update"));
    const isNotDeletable = nonDeletableSet.has(edition.id);

    const groups: ListItemAction[][] = [];

    const enrollGroup: ListItemAction[] = [];
    if (canEnroll) {
      enrollGroup.push({
        label: String(t("menu.enroll")),
        icon: <UserPlus size={16} />,
        onClick: () => handleEnroll(edition),
      });
    }
    if (canSendMail) {
      enrollGroup.push({
        label: String(t("menu.send-email")),
        icon: <Mail size={16} />,
        disabled: isPreparingMail,
        onClick: () => handleSendEmail(edition),
      });
    }
    if (enrollGroup.length) groups.push(enrollGroup);

    if (canViewDetail) {
      const navGroup: ListItemAction[] = [
        {
          label: String(t("menu.sessions")),
          icon: <Monitor size={16} />,
          onClick: () => goToDetail(edition, "sessions"),
        },
      ];
      if (canViewRegistry) {
        navGroup.push({
          label: String(t("menu.registry")),
          icon: <ClipboardList size={16} />,
          onClick: () => goToDetail(edition, "register"),
        });
      }
      navGroup.push({
        label: String(t("menu.enrolled-users")),
        icon: <Users size={16} />,
        onClick: () => goToDetail(edition, "enrolled-users"),
      });
      groups.push(navGroup);
    }

    const manageGroup: ListItemAction[] = [];
    if (canViewDetail) {
      manageGroup.push({
        label: String(t("menu.edit")),
        icon: <Pencil size={16} />,
        onClick: () => handleEdit(edition),
      });
    }
    if (canUpdate) {
      manageGroup.push({
        label: String(t("menu.delete.label")),
        icon: <Trash2 size={16} />,
        disabled: isNotDeletable,
        onClick: () => setEditionToDelete(edition),
      });
    }
    if (manageGroup.length) groups.push(manageGroup);

    return groups.length ? groups : undefined;
  };

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3, pr: 3 }}>
        <Breadcrumb />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: "28px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t("title")}
          </Typography>
          <PermissionGate
            permission={editionPerm(kind, "create")}
            mode="disable"
          >
            <Button
              variant="contained"
              startIcon={<Pencil size={16} />}
              onClick={handleCreate}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 700,
              }}
            >
              {t("create-button")}
            </Button>
          </PermissionGate>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          {EDITIONS_SEARCH_ENABLED && (
            <Box
              sx={{
                flex: "1 1 320px",
                minWidth: 0,
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                "& .MuiFormHelperText-root": { display: "none" },
                "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
              }}
              onKeyDown={handleSearchKeyDown}
            >
              <InputCst
                id="editions-search"
                label={String(t("search-placeholder"))}
                value={searchInput}
                onChange={(e: any) => setSearchInput(e.target.value)}
                endAdornment={
                  <Search size={16} color={theme.palette.text.tertiary} />
                }
              />
            </Box>
          )}

          <FiltersAccordionToggle
            pageId={
              kind === "physical"
                ? PagesID["corporate.physical-editions"]
                : PagesID["corporate.virtual-editions"]
            }
            value={fieldFilters}
            onApply={(next) => {
              setFieldFilters(next);
              setPage(1);
            }}
            excludeKeys={["learning_objects_id"]}
          />

          <Box sx={{ flex: 1 }} />
          <SortMenuCst
            id="editions-sort"
            value={sort}
            options={sortOptions}
            onChange={(v) => setSort(v)}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
              color: theme.palette.text.primary,
            }}
          >
            {t("empty")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {items.map((edition) => (
              <EditionCard
                key={edition.id}
                edition={edition}
                onManage={handleManage}
                menuActions={buildMenuActions(edition)}
              />
            ))}
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <PaginatorCst
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
            />
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            }}
          >
            {t("back")}
          </Button>
        </Box>

        {editionToDelete && (
          <ConfirmDialogCst
            open
            title={String(t("menu.delete.title"))}
            description={String(
              t("menu.delete.description", {
                name:
                  editionToDelete.edition_name ||
                  editionToDelete.edition_code ||
                  `#${editionToDelete.id}`,
              }),
            )}
            confirmLabel={String(t("menu.delete.confirm"))}
            cancelLabel={String(t("menu.delete.cancel"))}
            confirmVariant="danger"
            isSubmitting={isDeleting}
            submittingLabel={String(t("menu.delete.deleting"))}
            onClose={() => setEditionToDelete(null)}
            onConfirm={handleConfirmDelete}
          />
        )}
      </Box>
    </AnimationFadeIn>
  );
};

export default EditionsListPage;
